import { Entity } from '../entity';
import { DateUtility } from '../../utilities/date.utility';
import { AddressUtility } from '../../utilities/address.utility';
import { CodeCategory } from '../shared/code';
import { CodeType } from '../shared/code.type';
import { CustomFieldParent } from './custom.field.parent';
import { CustomFieldValue } from './custom.field.value';
import { MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import { CustomFieldType } from './custom.field.type';
import * as Enumerable from 'linq';

export enum CustomFieldHost {
    Web,
    FieldApp,
    ContractorPortal,
    ClientPortal
}

export interface CustomField extends Entity {
    type: CustomFieldType;
    options: any;

    createdAt?: Date;
    updatedAt?: Date;

    uses?: number;
}

export interface CustomFieldMapping {
    _id: string;
    active?: boolean;
    name: string;
    fieldId: string;
    parent: CustomFieldParent;
    defaultValue: any;

    jobTypes: Entity[];
    priorities: Entity[];
    itemTypes?: CodeType[];
    codeCategories?: CodeCategory[];
    workCategories?: Entity[];
    projectType?: Entity[];
    contactTypes?: Entity[];

    showOnNew: boolean;
    showInQuickView: boolean;
    showInFieldApp: boolean;
    allowEditInFieldApp: boolean;
    showInClientPortal: boolean;
    showInContractorPortal: boolean;
    allowEditInContractorPortal: boolean;
    required: boolean;
    readonly: boolean;
    searchable: boolean;
    sortOrder: number;

    createdAt?: Date;
    updatedAt?: Date;
}

export interface CustomFieldMappingEnriched extends CustomFieldMapping {
    field: CustomField;
    readonly: boolean;
}

export interface CustomFieldsDropdownGroup {
    name: string;
    items: string;
}

export interface CustomFieldsDropdownOptions {
    groups: CustomFieldsDropdownGroup[];
    doNotSort: boolean;
}

export interface CustomFieldsPickerOptions {
    multiple: boolean;
}

export interface CustomFieldsNumberOptions {
    min: number;
    max: number;
}

export interface CustomFieldTypeItem {
    _id: string;
    name: string;
}

export class CustomFieldUtility {

    private static readonly _types = [
        { _id: CustomFieldType.Address, name: 'Address' },
        { _id: CustomFieldType.Boolean, name: 'Boolean' },
        { _id: CustomFieldType.Date, name: 'Date' },
        { _id: CustomFieldType.DateTime, name: 'Date & Time' },
        { _id: CustomFieldType.Dropdown, name: 'Dropdown' },
        { _id: CustomFieldType.DropdownMulti, name: 'Dropdown Multi' },
        { _id: CustomFieldType.Email, name: 'Email' },
        { _id: CustomFieldType.Note, name: 'Note' },
        { _id: CustomFieldType.Number, name: 'Number' },
        { _id: CustomFieldType.Phone, name: 'Phone' },
        { _id: CustomFieldType.Text, name: 'Text' },
        { _id: CustomFieldType.Image, name: 'Image' },
        { _id: CustomFieldType.Link, name: 'Link' },
        // { _id: CustomFieldType.PickerEmployee, name: 'Employee' },
    ] as CustomFieldTypeItem[];

    public static getFieldId(type: CustomFieldType, name: string): string {
        name = name ?? '';
        return `${type.toLowerCase()}_${name.toLowerCase()}`;
    }

    public static getMappingId(fieldId: string, parent: CustomFieldParent): string {
        return `${fieldId}_${parent}`.toLowerCase();
    }

    public static getFieldType(fieldId: string): CustomFieldType {
        if (!fieldId || !fieldId?.length) return CustomFieldType.None;

        const typeKey = fieldId.split('_')[0];
        const type = Enumerable.from(this.types).where(x => x._id.toLocaleLowerCase() === typeKey).select(x => x._id).firstOrDefault() as CustomFieldType ?? CustomFieldType.None;

        return type;
    }

    private static readonly _customFieldMetadataType = {
        [CustomFieldType.Address]: MetadataEntityFieldType.Address,
        [CustomFieldType.Boolean]: MetadataEntityFieldType.Boolean,
        [CustomFieldType.Date]: MetadataEntityFieldType.Date,
        [CustomFieldType.DateTime]: MetadataEntityFieldType.DateTime,
        [CustomFieldType.Dropdown]: MetadataEntityFieldType.CustomFieldDropdown,
        [CustomFieldType.DropdownMulti]: MetadataEntityFieldType.CustomFieldDropdown,
        [CustomFieldType.Email]: MetadataEntityFieldType.Email,
        [CustomFieldType.Note]: MetadataEntityFieldType.String,
        [CustomFieldType.Number]: MetadataEntityFieldType.Number,
        [CustomFieldType.Phone]: MetadataEntityFieldType.Phone,
        [CustomFieldType.Text]: MetadataEntityFieldType.String,
        [CustomFieldType.Image]: MetadataEntityFieldType.Image,
        [CustomFieldType.Link]: MetadataEntityFieldType.Link,
        // { _id: CustomFieldType.PickerEmployee, name: 'Employee' },
    };

    public static getMetadataFieldType(fieldId: string): MetadataEntityFieldType {
        const type = this.getFieldType(fieldId);
        return this._customFieldMetadataType[type] ?? MetadataEntityFieldType.None;
    }

    public static getFieldValue(type: CustomFieldType, value: any): string {

        // if (type !== CustomFieldType.Boolean && type !== CustomFieldType.Address && type !== CustomFieldType.Image) {
        if (![CustomFieldType.Boolean, CustomFieldType.Address, CustomFieldType.Image, , CustomFieldType.Link].includes(type)) {
            // ignore the image type if no value
            if (!value) {
                return 'Not Set';
            }

            if (Array.isArray(value)) {
                if (!value?.length) return 'Not Set';
            }
        }

        switch (type) {
            case CustomFieldType.Boolean:
                return value ? 'Yes' : 'No';
            case CustomFieldType.Date:
                return DateUtility.formatDateTime(value, 'utc', 'dd/MM/yy');
            case CustomFieldType.DateTime:
                const zone = DateUtility.getZoneFormIsoString(value?.toString());
                return DateUtility.formatDateTime(value, zone, 'dd/MM/yy hh:mm a');
            case CustomFieldType.Address: {
                const address = AddressUtility.formatAddress(value);
                return address ? address : 'Not Set';
            }
            case CustomFieldType.DropdownMulti: {
                return (value as string[])?.join(', ');
            }
            // case CustomFieldType.Link: {
            //     // Link value is an object { text, url }; show the display text, falling back to the raw url.
            //     return value?.text || value?.url || null;
            // }
            // case CustomFieldType.PickerEmployee: {
            //     let result = 'Not Set';
            //     if (Array.isArray(value)) {
            //         const emps = Enumerable.from(value as Entity[]).select(x => x.name).toArray();
            //         if (emps?.length > 0) result = emps.join(', ');
            //     } else {
            //         const emp = (value as Entity)?.name;
            //         if (emp) result = emp;
            //     }
            //     return result;
            // }
            default:
                return value;
        }
    }

    public static getFieldMergeValues(customFields: any): CustomFieldValue[] {

        const fieldValues = customFields
            ? Enumerable
                .from(Object.keys(customFields))
                .select(key => {
                    const fieldValue = customFields[key];
                    const fieldType = CustomFieldUtility.getFieldType(key);
                    const fieldMergeValue = CustomFieldUtility.getFieldValue(fieldType, fieldValue);
                    return { name: key, value: fieldMergeValue } as CustomFieldValue;
                })
                .toArray()
            : [];

        return fieldValues;
    }

    public static get types(): CustomFieldTypeItem[] {
        return CustomFieldUtility._types;
    }

    /**
    * 
    * @param inputCustomFieldsValues Source custom fields values
    * @param allowedCustomFields Custom fields definitions for destination model
    * @returns custom fields values which match destination model allowed field formats, return undefined if no match was found
    */
    public static cloneCustomFieldsValue(inputCustomFieldsValues: null | Object, allowedCustomFields: CustomFieldMapping[]): any {

        if (!inputCustomFieldsValues) return undefined;

        const result = allowedCustomFields.reduce((acc, curr) => {
            if (inputCustomFieldsValues.hasOwnProperty(curr.fieldId)) {
                acc[curr.fieldId] = inputCustomFieldsValues[curr.fieldId];
            }
            return acc;
        }, {});

        return Object.keys(result).length > 0 ? result : undefined;
    }

}
